/**
 * RTK Query API for Plan Guide.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md
 * @see frontend/workspace/CU-868gzzjua/frontend_implementation_guide.md
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { carBaseQuery } from '@app/api/carBaseQuery';
import type { PageResponse, ActionPayload, GuideSession } from '../types';
import { API_BASE_PATH } from '../constants';

interface PageRequest {
  planResultRootId: string;
  planResultTypeId: string;
  pageId: string;
  session?: GuideSession;
}

interface ActionRequest {
  planResultRootId: string;
  planResultTypeId: string;
  action: ActionPayload;
  session?: GuideSession;
}

interface TaskStatusRequest {
  planResultRootId: string;
  planResultTypeId: string;
  taskId: string;
}

interface TaskStatusResponse {
  status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  thinking?: {
    step_title: string;
    step_description: string;
    progress_percent: number;
  };
  result?: any;
  error?: string;
}

export const planGuideApi = createApi({
  reducerPath: 'planGuideApi',
  baseQuery: carBaseQuery,
  endpoints: (builder) => ({
    // GET page
    getPage: builder.query<PageResponse, PageRequest>({
      query: ({ planResultRootId, planResultTypeId, pageId, session }) => {
        const url = `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/page/${pageId}/`;
        // Add session as query parameter if provided
        if (session) {
          const params = new URLSearchParams();
          params.append('session', JSON.stringify(session));
          return {
            url: `${url}?${params.toString()}`,
            method: 'GET',
          };
        }
        return {
          url,
          method: 'GET',
        };
      },
    }),

    // POST action
    handleAction: builder.mutation<PageResponse, ActionRequest>({
      query: ({ planResultRootId, planResultTypeId, action, session }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/action/`,
        method: 'POST',
        body: { ...action, session },
      }),
    }),

    // GET task status for polling
    getPlanGuideTaskStatus: builder.query<TaskStatusResponse, TaskStatusRequest>({
      query: ({ planResultRootId, planResultTypeId, taskId }) => ({
        url: `${API_BASE_PATH}/${planResultRootId}/${planResultTypeId}/task/${taskId}/`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetPageQuery,
  useLazyGetPageQuery,
  useHandleActionMutation,
  useGetPlanGuideTaskStatusQuery,
  useLazyGetPlanGuideTaskStatusQuery,
} = planGuideApi;
